/**
 * Media Master (MM) — loads scoped JSON registries, caches resolutions, delegates to Orchestrator.
 */
import { logger } from '@/shared/utils';
import { orchestrateResolve } from './mediaOrchestrator';
import { loadScopedMediaRegistry } from './registryLoader';
import type { MediaEntry, MediaRegistryMap, ResolvedMedia } from './types';

const log = (msg: string, ctx?: Record<string, unknown>) =>
  logger.debug(msg, 'MediaMaster', ctx);

export class MediaMaster {
  private readonly registryCache = new Map<string, MediaRegistryMap>();
  private readonly resolvedCache = new Map<string, ResolvedMedia>();
  private readonly loadingScopes = new Map<string, Promise<MediaRegistryMap>>();

  private cacheKey(scope: string, variable: string): string {
    return `${scope}::${variable}`;
  }

  /** Preload a page scope registry (call from route loaders or page mount). */
  async preloadScope(scope: string): Promise<void> {
    await this.ensureScopeLoaded(scope);
  }

  private async ensureScopeLoaded(scope: string): Promise<MediaRegistryMap> {
    if (this.registryCache.has(scope)) {
      return this.registryCache.get(scope)!;
    }
    const pending = this.loadingScopes.get(scope);
    if (pending) return pending;

    const promise = loadScopedMediaRegistry(scope)
      .then((map) => {
        this.registryCache.set(scope, map);
        this.loadingScopes.delete(scope);
        log('Registry loaded', { scope, keys: Object.keys(map).length });
        return map;
      })
      .catch((e) => {
        this.loadingScopes.delete(scope);
        logger.error('Failed to load media registry', 'MediaMaster', { scope, error: e });
        throw e;
      });

    this.loadingScopes.set(scope, promise);
    return promise;
  }

  /**
   * Resolve a variable for a scope. Lazy-loads registry on first use; caches result.
   */
  async getMedia(scope: string, variable: string): Promise<ResolvedMedia> {
    const key = this.cacheKey(scope, variable);
    const cached = this.resolvedCache.get(key);
    if (cached) return cached;

    const registry = await this.ensureScopeLoaded(scope);
    const entry = registry[variable];
    if (!entry) {
      const msg = `[MediaMaster] No entry for variable "${variable}" in scope "${scope}"`;
      logger.error(msg, 'MediaMaster', { scope, variable });
      throw new Error(msg);
    }

    const resolved = orchestrateResolve(scope, variable, entry);
    this.resolvedCache.set(key, resolved);
    return resolved;
  }

  /**
   * Synchronous read if registry and resolution are already cached (e.g. after preload).
   */
  getMediaIfCached(scope: string, variable: string): ResolvedMedia | null {
    return this.resolvedCache.get(this.cacheKey(scope, variable)) ?? null;
  }

  /** Drop a cached resolution so the next getMedia refetches (e.g. after registry edit). */
  invalidateResolution(scope: string, variable: string): void {
    this.resolvedCache.delete(this.cacheKey(scope, variable));
  }

  /** Clear caches (e.g. hot reload in dev). */
  clearCaches(): void {
    this.registryCache.clear();
    this.resolvedCache.clear();
    this.loadingScopes.clear();
  }

  /** Register runtime entry (tests / dynamic patches). */
  registerEntry(scope: string, variable: string, entry: MediaEntry): ResolvedMedia {
    const map = this.registryCache.get(scope) ?? {};
    map[variable] = entry;
    this.registryCache.set(scope, map);
    const resolved = orchestrateResolve(scope, variable, entry);
    this.resolvedCache.set(this.cacheKey(scope, variable), resolved);
    return resolved;
  }
}

export const mediaMaster = new MediaMaster();

/** Route/layout helper — preload registry without resolving variables. */
export async function preloadMediaScope(scope: string): Promise<void> {
  await mediaMaster.preloadScope(scope);
}
